import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Download, FileSpreadsheet, FileText, Image as ImageIcon, Loader2 } from "lucide-react";
import api from "@/lib/api";

export const SupportAttachment = ({ ticketId, attachment, dark, admin }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [thumb, setThumb] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const image = attachment.mime_type.startsWith("image/");
  const pdf = attachment.mime_type === "application/pdf";
  const fileUrl = `/support/tickets/${ticketId}/attachments/${attachment.id}`;
  const viewerUrl = `${admin ? "/admin/assistenza" : "/assistenza"}/${ticketId}/allegato/${attachment.id}`;

  useEffect(() => {
    if (!image) return undefined;
    if (!("IntersectionObserver" in window)) { setVisible(true); return undefined; }
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) { setVisible(true); observer.disconnect(); }
    }, { rootMargin: "180px" });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [image]);

  useEffect(() => {
    if (!image || !visible) return undefined;
    const controller = new AbortController();
    let url = "";
    let active = true;
    api.get(fileUrl, { responseType: "blob", signal: controller.signal }).then(({ data }) => {
      if (!active) return;
      url = URL.createObjectURL(new Blob([data], { type: attachment.mime_type }));
      setThumb(url);
    }).catch(() => { if (active) setError("Anteprima non disponibile"); });
    return () => { active = false; controller.abort(); if (url) URL.revokeObjectURL(url); };
  }, [fileUrl, image, visible, attachment.mime_type]);

  const downloadExcel = async () => {
    if (busy) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.get(fileUrl, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: attachment.mime_type }));
      const link = document.createElement("a");
      link.href = url; link.download = attachment.name;
      document.body.appendChild(link); link.click(); link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { setError("Allegato non disponibile. Riprova."); }
    finally { setBusy(false); }
  };

  const content = <>
    {image ? (
      <span className="block w-28 h-24 sm:w-36 sm:h-28 border border-current/20 overflow-hidden bg-white/10">
        {thumb ? <img src={thumb} alt={`Anteprima ${attachment.name}`} loading="lazy"
                      className="w-full h-full object-contain" data-testid={`support-attachment-thumb-${attachment.id}`} />
               : <ImageIcon className="w-6 h-6 m-auto mt-8 opacity-60" />}
      </span>
    ) : pdf ? <FileText className="w-5 h-5 shrink-0" />
      : busy ? <Loader2 className="w-5 h-5 shrink-0 animate-spin" />
        : <FileSpreadsheet className="w-5 h-5 shrink-0" />}
    <span className="flex items-center gap-1.5 min-w-0 text-xs">
      <span className="truncate">{attachment.name}</span>
      {!image && !pdf && <Download className="w-3.5 h-3.5 shrink-0" />}
    </span>
  </>;

  const style = `inline-flex flex-col items-start gap-2 max-w-full text-left hover:opacity-80 transition-opacity ${dark ? "text-[#D9E9D4]" : "text-[#325E44]"}`;
  return <div ref={ref} className="min-w-0" data-testid={`support-attachment-${attachment.id}`}>
    {image || pdf ? (
      <Link to={viewerUrl} target="_blank" rel="noopener noreferrer" className={style}
            title={`Apri ${attachment.name} in una nuova scheda`}
            data-testid={`support-attachment-open-${attachment.id}`}>{content}</Link>
    ) : (
      <button type="button" onClick={downloadExcel} disabled={busy} className={style}
              title={`Scarica ${attachment.name}`} data-testid={`support-attachment-open-${attachment.id}`}>{content}</button>
    )}
    {error && <p role="alert" className="text-xs text-[#A93333] mt-1"
                 data-testid={`support-attachment-error-${attachment.id}`}>{error}</p>}
  </div>;
};